import { createContext, use, useEffect, useState, type ReactNode } from 'react';
import { activityPayload, activityStates, HEARTBEAT_MS, parseActivitySnapshot,
  type ActivitySnapshot, type ActivityState } from './activityLease';
import './activity-presence.css';

const ActivityContext = createContext<Map<number, ActivityState> | null | undefined>(undefined);
const labels: Record<ActivityState, string> = {
  active: 'Активен в Скрепке', away: 'Отошёл — Скрепка открыта',
  offline: 'Не в сети в Скрепке', unknown: 'Статус в Скрепке пока неизвестен',
};

export function ActivityPresenceProvider({ children }: { children: ReactNode }) {
  const [states, setStates] = useState<Map<number, ActivityState> | null>(null);
  useEffect(() => {
    const session = crypto.randomUUID();
    let stopped = false, suspended = false, busy = false;
    let lastAction = Date.now(), lastSent = 0, signature = '', sequence = 0;
    let snapshot: ActivitySnapshot | null = null;
    let controller: AbortController | null = null;
    const foreground = () => document.visibilityState === 'visible' && document.hasFocus();
    const publish = () => {
      const next = activityStates(snapshot, Date.now());
      const key = next === null ? '?' : JSON.stringify([...next].sort((a, b) => a[0] - b[0]));
      if (key !== signature && !stopped) { signature = key; setStates(next); }
    };
    const beat = async (force = false) => {
      if (stopped || suspended || busy || (!force && Date.now() - lastSent < HEARTBEAT_MS)) return;
      busy = true; lastSent = Date.now(); controller = new AbortController();
      const timeout = setTimeout(() => controller?.abort(), 8_000);
      try {
        const response = await fetch('/api/user-presence', {
          method: 'POST', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', 'X-Skrepka-Presence': '1' },
          body: JSON.stringify({ ...activityPayload(session, lastAction, Date.now(), foreground()), sequence: ++sequence }),
        });
        if (!response.ok) throw new Error('Activity unavailable');
        const value: unknown = await response.json();
        if (!stopped && !suspended) snapshot = parseActivitySnapshot(value, Date.now());
      } catch { /* Retain a brief verified lease, then mark status unknown. */ }
      finally { clearTimeout(timeout); busy = false; publish(); }
    };
    const action = () => { if (foreground()) { lastAction = Date.now(); void beat(); } };
    const visibility = () => { if (foreground()) lastAction = Date.now(); void beat(true); };
    const close = () => {
      suspended = true; controller?.abort();
      void fetch('/api/user-presence', {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'X-Skrepka-Presence': '1' },
        body: JSON.stringify({ session, closed: true, sequence: ++sequence }),
      }).catch(() => undefined);
    };
    const resume = () => { suspended = false; if (foreground()) lastAction = Date.now(); void beat(true); };
    const events = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'];
    for (const event of events) window.addEventListener(event, action, { passive: true, capture: true });
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('focus', visibility); window.addEventListener('blur', visibility);
    window.addEventListener('pagehide', close); window.addEventListener('pageshow', resume);
    window.addEventListener('online', resume);
    const timer = setInterval(() => { publish(); void beat(); }, 1000);
    void beat(true);
    return () => {
      stopped = true; clearInterval(timer); close();
      for (const event of events) window.removeEventListener(event, action, true);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('focus', visibility); window.removeEventListener('blur', visibility);
      window.removeEventListener('pagehide', close); window.removeEventListener('pageshow', resume);
      window.removeEventListener('online', resume);
    };
  }, []);
  return <ActivityContext value={states}>{children}</ActivityContext>;
}

export function ActivityDot({ userId }: { userId?: number | null }) {
  const states = use(ActivityContext);
  if (!userId || states === undefined) return null;
  const state: ActivityState = states === null ? 'unknown' : states.get(userId) ?? 'offline';
  return <span className={`activity-dot activity-dot--${state}`} title={labels[state]}
    aria-label={labels[state]} role="img" />;
}
